import React, { useContext, useEffect, useMemo, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceIcon from "@mui/icons-material/Place";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";

// Dispatch timestamps are stored as millisecond strings (see
// MOBILE_DISPATCH_IMPLEMENTATION.md). Coerce safely to a Date.
const toDate = (value) => {
  if (!value) return null;
  const ms = typeof value === "string" ? parseInt(value, 10) : Number(value);
  if (Number.isNaN(ms)) return null;
  return new Date(ms);
};

const formatTime = (date) =>
  date
    ? date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "";

const toDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const DispatchCard = ({ dispatch }) => {
  const start = toDate(dispatch.startTime);
  const end = toDate(dispatch.endTime);
  const task = dispatch.isRoom ? "Room Turn" : dispatch.primary || "Task";
  const location = dispatch.isRoom ? dispatch.room : dispatch.secondary;

  const checklists = dispatch.checklists || [];
  const totalItems = checklists.reduce(
    (sum, cl) => sum + (cl.items?.length || 0),
    0
  );
  const completedItems = checklists.reduce(
    (sum, cl) => sum + (cl.items?.filter((i) => i.completed).length || 0),
    0
  );
  const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  const isComplete = totalItems > 0 && completedItems === totalItems;

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Typography variant="subtitle1" fontWeight={600}>
          {task}
        </Typography>
        {(start || end) && (
          <Chip
            size="small"
            icon={<AccessTimeIcon />}
            label={`${formatTime(start)}${end ? ` – ${formatTime(end)}` : ""}`}
          />
        )}
      </Box>
      {location && (
        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
          <PlaceIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {location}
          </Typography>
        </Box>
      )}
      {dispatch.notes && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {dispatch.notes}
        </Typography>
      )}
      {totalItems > 0 && (
        <Box mt={1.5}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Checklist
            </Typography>
            <Typography
              variant="caption"
              color={isComplete ? "success.main" : "text.secondary"}
            >
              {completedItems}/{totalItems}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={isComplete ? "success" : "primary"}
          />
        </Box>
      )}
    </Card>
  );
};

const MyDispatch = ({ userId }) => {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [date, setDate] = useState(() => new Date());
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const dateKey = useMemo(() => toDateKey(date), [date]);

  useEffect(() => {
    if (!selectedProperty || !userId) return undefined;
    setLoading(true);
    const dispatchRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "dispatches",
      dateKey,
      userId
    );
    const unsubscribe = onSnapshot(
      dispatchRef,
      (snapshot) => {
        const rows = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const at = toDate(a.startTime)?.getTime() || 0;
            const bt = toDate(b.startTime)?.getTime() || 0;
            return at - bt;
          });
        setDispatches(rows);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading dispatch:", error);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [selectedProperty, userId, dateKey]);

  const shiftDay = (delta) =>
    setDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + delta);
      return next;
    });

  const isToday = toDateKey(new Date()) === dateKey;
  const dateLabel = date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 720, mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        My Dispatch
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <IconButton onClick={() => shiftDay(-1)}>
          <ChevronLeftIcon />
        </IconButton>
        <Box textAlign="center">
          <Typography variant="subtitle1" fontWeight={600}>
            {dateLabel}
          </Typography>
          {isToday && (
            <Typography variant="caption" color="primary">
              Today
            </Typography>
          )}
        </Box>
        <IconButton onClick={() => shiftDay(1)}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : dispatches.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={8}
          color="text.secondary"
        >
          <EventBusyIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <Typography>No dispatch assigned for this day</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {dispatches.map((d) => (
            <DispatchCard key={d.id} dispatch={d} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default MyDispatch;
